import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, FileDown, Loader2, ChevronRight } from 'lucide-react';
import { oliApi, erroreOli, scaricaFascicolo } from './api';

const anni = () => { const y = new Date().getFullYear(); return [y, y - 1, y - 2]; };

const useVerifiche = (anno, aggiorna) => {
  const [dati, setDati] = useState(null);
  const carica = useCallback(async () => {
    try { setDati(await oliApi.get(`/verifiche?anno=${anno}`)); } catch (e) { setDati(null); }
  }, [anno]);
  useEffect(() => { carica(); }, [carica, aggiorna]);
  return dati;
};

// Riquadro in cima alla sezione: quanti punti contesterebbe un ispettore oggi
export const BannerVerifiche = ({ aggiorna, onApri }) => {
  const dati = useVerifiche(new Date().getFullYear(), aggiorna);
  if (!dati) return null;
  const { gravi, attenzione } = dati.riepilogo;
  const ok = gravi + attenzione === 0;
  return (
    <button onClick={onApri} data-testid="oli-banner-verifiche"
      className={`w-full text-left rounded-xl p-4 border flex items-center gap-3 ${ok ? 'bg-emerald-50 border-emerald-200' : gravi ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      {ok ? <ShieldCheck className="h-6 w-6 text-emerald-600" /> : <ShieldAlert className={`h-6 w-6 ${gravi ? 'text-red-600' : 'text-amber-600'}`} />}
      <div className="flex-1">
        <p className="font-semibold text-sm text-gray-900">{ok ? 'Registro in ordine' : `${gravi} da sistemare subito, ${attenzione} da controllare`}</p>
        <p className="text-xs text-gray-600">Controllo come farebbe un ispettore · fascicolo PDF da mostrare</p>
      </div>
      <ChevronRight className="h-5 w-5 text-gray-400" />
    </button>
  );
};

// Scheda "Controllo": elenco dei punti e fascicolo PDF per l'ispettore
export const VerificheOli = ({ aggiorna }) => {
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [scarico, setScarico] = useState(false);
  const dati = useVerifiche(anno, aggiorna);

  const pdf = async () => {
    setScarico(true);
    try { await scaricaFascicolo(anno); } catch (e) { toast.error(erroreOli(e, 'Fascicolo non disponibile')); } finally { setScarico(false); }
  };

  return (
    <div className="space-y-3" data-testid="oli-verifiche">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-900">Controllo del registro</h3>
        <div className="flex gap-2">
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="h-10 px-3 rounded-lg border border-gray-300 text-sm" data-testid="oli-verifiche-anno">
            {anni().map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={pdf} disabled={scarico} data-testid="oli-fascicolo-pdf"
            className="h-10 px-4 rounded-lg bg-blue-900 hover:bg-blue-800 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60">
            {scarico ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Fascicolo PDF
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">Il PDF contiene verifiche, autorizzazioni, registro numerato dei ritiri, correzioni, controlli dell'olio di frittura e in coda le copie dei FIR.</p>
      {dati && (
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="oli-verifiche-riepilogo">
          <div className="bg-white rounded-xl border border-gray-200 p-3"><p className="text-xl font-bold text-gray-900">{dati.riepilogo.ritiri}</p><p className="text-xs text-gray-500">ritiri</p></div>
          <div className="bg-white rounded-xl border border-gray-200 p-3"><p className="text-xl font-bold text-gray-900">{dati.riepilogo.kg}</p><p className="text-xs text-gray-500">kg conferiti</p></div>
          <div className="bg-white rounded-xl border border-gray-200 p-3"><p className="text-xl font-bold text-gray-900">{dati.riepilogo.copie_ricevute}/{dati.riepilogo.ritiri}</p><p className="text-xs text-gray-500">copie firmate</p></div>
        </div>
      )}
      {dati && !dati.verifiche.length && <p className="text-sm text-emerald-700" data-testid="oli-verifiche-ok">Nessun punto da segnalare.</p>}
      <div className="space-y-2">
        {(dati?.verifiche || []).map((v, i) => (
          <div key={i} data-testid={`oli-verifica-${i}`}
            className={`rounded-xl p-3 border text-sm ${v.livello === 'grave' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
            <span className="font-semibold">{v.livello === 'grave' ? 'Da sistemare: ' : 'Da controllare: '}</span>{v.testo}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400">Strumento di supporto: la responsabilità della tenuta dei registri resta del titolare.</p>
    </div>
  );
};
